import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
} from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SpeedIcon from "@mui/icons-material/Speed";
import SavingsIcon from "@mui/icons-material/Savings";
import SearchIcon from "@mui/icons-material/Search";
import PsychologyIcon from "@mui/icons-material/Psychology";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForward";
import LaunchIcon from "@mui/icons-material/Launch";
import InfoBox from "../utils/InfoBox";
import { getImageUrl } from "../../../utils/helpers";

const KthAiPoweredPatentAnalysis = ({ baseConfig, sectionStyles }) => {
  const {
    priColor,
    priHoverColor,
    tertColor,
    tertHoverColor,
    secColor,
    quadColor,
    quadHoverColor,
    linkColor,
  } = baseConfig.siteBranding;

  return (
    <Card sx={{ ...sectionStyles.card, mb: 2 }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          backgroundColor: `${tertColor}22`,
        }}
      >
        <SmartToyIcon sx={{ mr: 2, color: tertColor }} />
        <Typography variant="h6" fontWeight="bold">
          AI-Powered Patent Analysis
        </Typography>
      </Box>
      <CardContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="body1" paragraph>
              Our AI-enhanced patent analysis services leverage cutting-edge
              artificial intelligence tools with attorney oversight to deliver
              faster, more cost-effective, and more comprehensive results than
              traditional manual methods alone. This attorney-in-the-loop
              approach ensures you get the benefits of AI efficiency while
              maintaining the quality and judgment of human legal expertise.
            </Typography>

            <Typography variant="body1" paragraph>
              By combining advanced machine learning algorithms with our
              technical and legal expertise, we can analyze vast patent
              databases, identify relevant prior art, detect potential
              infringement, and generate actionable insights about your patent
              portfolio with unprecedented speed and accuracy.
            </Typography>

            <Box sx={{ mt: 3 }}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                gutterBottom
                color={tertColor}
              >
                Core AI-Powered Analysis Services:
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: `1px solid ${tertColor}33`,
                      borderLeft: `4px solid ${tertColor}`,
                      borderRadius: 1,
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Prior Art Searching
                    </Typography>
                    <Typography variant="body2">
                      Our AI tools scan millions of patents, scientific
                      literature, and technical documents to identify relevant
                      prior art with semantic understanding beyond traditional
                      keyword searches.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: `1px solid ${tertColor}33`,
                      borderLeft: `4px solid ${tertColor}`,
                      borderRadius: 1,
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Patent Landscape Mapping
                    </Typography>
                    <Typography variant="body2">
                      AI-powered visualization and clustering techniques
                      generate comprehensive technology landscape maps,
                      highlighting competitive positioning and white space
                      opportunities.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: `1px solid ${tertColor}33`,
                      borderLeft: `4px solid ${tertColor}`,
                      borderRadius: 1,
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Infringement Analysis
                    </Typography>
                    <Typography variant="body2">
                      Machine learning algorithms assist in comparing patent
                      claims to potentially infringing products, accelerating
                      the identification of relevant features for thorough legal
                      analysis.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: `1px solid ${tertColor}33`,
                      borderLeft: `4px solid ${tertColor}`,
                      borderRadius: 1,
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Portfolio Optimization
                    </Typography>
                    <Typography variant="body2">
                      AI analytics evaluate patent strength, coverage gaps, and
                      strategic alignment with business objectives to optimize
                      patent investments and prioritize future filings.
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>

            <Box
              sx={{
                p: 2,
                mt: 3,
                backgroundColor: "#e8f5e9",
                borderRadius: 2,
                border: "1px solid #81c784",
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                gutterBottom
                color="#2e7d32"
              >
                Attorney-in-the-Loop Approach
              </Typography>
              <Typography variant="body2">
                While AI technology dramatically enhances our patent analysis
                capabilities, we maintain attorneys and technical experts in the
                loop throughout the process. This hybrid approach ensures that
                AI insights are validated by human expertise, providing you with
                reliable, actionable intelligence you can trust for critical
                business decisions.
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <img
                src={getImageUrl("kth-ai-in-patent-analysis.png")}
                alt="AI-Powered Patent Analysis"
                style={{
                  width: "100%",
                  maxWidth: "450px",
                  height: "auto",
                  borderRadius: "8px",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                }}
              />
            </Box>

            <Box
              sx={{
                p: 3,
                backgroundColor: "#f5f5f5",
                borderRadius: 2,
                border: `1px solid ${tertColor}33`,
              }}
            >
              <Typography variant="h6" gutterBottom color={tertColor}>
                Key Benefits of AI-Enhanced Patent Analysis
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      backgroundColor: "white",
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                      },
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <SpeedIcon sx={{ color: tertColor, mr: 1 }} />
                        <Typography variant="subtitle2" fontWeight="bold">
                          50-70% Faster Results
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        Dramatically reduced turnaround times compared to
                        traditional manual analysis methods, enabling faster
                        business decision-making.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      backgroundColor: "white",
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                      },
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <SavingsIcon sx={{ color: tertColor, mr: 1 }} />
                        <Typography variant="subtitle2" fontWeight="bold">
                          Significant Cost Savings
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        Lower costs through automated processes while
                        maintaining or improving quality through expert
                        oversight and validation.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      backgroundColor: "white",
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                      },
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <SearchIcon sx={{ color: tertColor, mr: 1 }} />
                        <Typography variant="subtitle2" fontWeight="bold">
                          Comprehensive Coverage
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        AI tools can analyze millions of patents and documents,
                        ensuring nothing relevant is missed in searches and
                        analyses.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      backgroundColor: "white",
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                      },
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <PsychologyIcon sx={{ color: tertColor, mr: 1 }} />
                        <Typography variant="subtitle2" fontWeight="bold">
                          Semantic Understanding
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        Advanced AI identifies conceptual similarities beyond
                        keyword matching, finding relevant results traditional
                        searches would miss.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>

        {/* Additional AI Patent Services Section */}
        <Box sx={{ mt: 4 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              color: tertColor,
              borderBottom: `1px solid ${tertColor}33`,
              pb: 1,
            }}
          >
            Additional AI & Patent Services
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={7}>
              <Typography variant="body1" paragraph>
                Beyond patent analysis, we offer specialized services at the
                intersection of artificial intelligence and patent law. Our
                technical expertise in both domains allows us to provide unique
                guidance for companies developing AI innovations or leveraging
                AI in their patent processes.
              </Typography>

              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="AI Patentability Assessment"
                    secondary="Navigate the complex landscape of AI patent eligibility under current legal frameworks"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Strategic AI Patent Drafting"
                    secondary="Craft patent applications for AI innovations that maximize protection while addressing subject matter eligibility challenges"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="AI Tool Selection & Implementation"
                    secondary="Guidance on selecting and integrating AI tools into your patent processes"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Data Privacy & IP Considerations"
                    secondary="Navigate the complex interplay between data usage for AI training and intellectual property rights"
                  />
                </ListItem>
              </List>

              <Box sx={{ display: "flex", mt: 3 }}>
                <Button
                  component={RouterLink}
                  to="/patent-ai-resources"
                  variant="contained"
                  sx={{
                    backgroundColor: tertColor,
                    color: "white",
                    "&:hover": {
                      color: "white",
                      backgroundColor: tertHoverColor,
                    },
                    mx: 1,
                  }}
                  endIcon={<LaunchIcon />}
                >
                  Patent AI Resources
                </Button>
                <Button
                  component={RouterLink}
                  to="/ai"
                  variant="outlined"
                  sx={{
                    borderColor: tertColor,
                    color: tertColor,
                    "&:hover": {
                      borderColor: tertHoverColor,
                      backgroundColor: `${tertColor}15`,
                    },
                    mx: 1,
                  }}
                  endIcon={<LaunchIcon />}
                >
                  AI Practice
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box sx={{ textAlign: "center" }}>
                <img
                  src={getImageUrl("ai-patents.png")}
                  alt="AI & Machine Learning Patents"
                  style={{
                    width: "100%",
                    maxWidth: "450px",
                    height: "auto",
                    borderRadius: "8px",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Case Study/Success Story */}
        <InfoBox
          title="Success Story: AI-Powered Patent Landscape Analysis"
          content={
            <Typography variant="body2">
              A technology startup approached us for strategic patent guidance
              before their Series B funding round. Using our AI-powered patent
              analysis tools, we rapidly mapped the competitive landscape across
              more than 5,000 patents, identifying both crowded areas to avoid
              and strategic white space opportunities. The analysis helped the
              company refocus their R&D efforts and patent strategy, resulting
              in several new patent applications targeting high-value,
              less-crowded technical areas. Investors specifically cited the
              company's refined IP strategy as a factor in their successful
              funding round.
            </Typography>
          }
          type="success"
        />
      </CardContent>
    </Card>
  );
};

export default KthAiPoweredPatentAnalysis;
